import * as React from 'react';
import { CornersIn, CornersOut } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import {
  listDetailLayoutDetailRecipe,
  listDetailLayoutListRecipe,
  listDetailLayoutRecipe,
  listDetailLayoutSearchRecipe,
  type ListDetailLayoutRecipeProps,
} from '../recipes/list-detail.recipe';
import { Button } from './Button';
import { Inline, Show } from './Layout';
import { Text } from './Text';

const ExpandContext = React.createContext<{
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
} | null>(null);

/**
 * The button that gives a record the screen. ListDetailLayout says what it
 * does; it draws nothing outside a layout that offers full screen, as when
 * Reports opens a call in a dialog, and nothing below `lg`, where the record
 * already has the width. RecordHeading draws it; the impound's number editor
 * is the one header that places it alone.
 */
export function RecordExpandButton() {
  const expand = React.useContext(ExpandContext);
  if (!expand) return null;
  const label = expand.expanded ? 'Exit full screen' : 'Full screen';
  return (
    <Show above="lg" display="flex" className="shrink-0">
      <Button
        variant="outline"
        size="icon-sm"
        title={label}
        aria-label={label}
        onClick={() => expand.onExpandedChange(!expand.expanded)}
      >
        {expand.expanded ? <CornersIn /> : <CornersOut />}
      </Button>
    </Show>
  );
}

export interface RecordHeadingProps {
  /** What it is, in a word: "Call", "Impound". */
  type: string;
  /** Which one, as people say it: "CALL-0118". Left out while it loads. */
  id?: React.ReactNode;
  /** Leave the full-screen button out: a report inside a truck's card. */
  expand?: boolean;
  /** Beside the words: a status, a pencil. */
  children?: React.ReactNode;
}

/**
 * The top of every record opened from a list: the full-screen button, then
 * the type and the id, in one size.
 *
 *   <RecordHeading type="Call" id="CALL-0118">
 *     <Badge tone="warning">Urgently</Badge>
 *   </RecordHeading>
 *
 * Each record wrote its own: "Call Details", "Impound Details — IMP-1", a
 * label over a smaller name, and for a driver, nothing.
 */
export function RecordHeading({
  type,
  id,
  expand = true,
  children,
}: RecordHeadingProps) {
  return (
    <Inline gap={3} align="center" className="min-w-0">
      {expand ? <RecordExpandButton /> : null}
      <Text variant="h2" as="h2" truncate>
        {type}
        {id ? <> {id}</> : null}
      </Text>
      {children}
    </Inline>
  );
}

export interface ListDetailLayoutProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ListDetailLayoutRecipeProps {
  /** The left column: a ListToolbar over bands and rows. Flush — no card. */
  list: React.ReactNode;
  /**
   * A ListSearch, held above the rows as they scroll: as wide as the list, so
   * the record keeps the room beside it.
   */
  search?: React.ReactNode;
  /** The right column: the record's card, or what to show when there is none. */
  detail: React.ReactNode;
  /**
   * Inside a page that already insets its content, as the dashboard's does:
   * the list takes back 16px so its toolbar and bands line up with the
   * title. Pair with `<ListColumn bleed>` for the same screen's Table view.
   */
  bleed?: boolean;
  /** The record has the screen: the list is gone from `lg` up. */
  expanded?: boolean;
  /**
   * Offers full screen: the record's header draws the control, with a
   * RecordExpandButton. Leave it out when there is no record to expand, or
   * for a list that has to stay in sight.
   */
  onExpandedChange?: (expanded: boolean) => void;
}

/**
 * A grouped list beside the record picked from it.
 *
 *   <Page fill="lg">
 *     <Tabs fill="lg">
 *       <TabsContent value="reports" fill="lg">
 *         <ListDetailLayout scroll list={…} detail={<ReportCard />} />
 *
 * The list is 400 wide from `lg` up and full width below, where the two
 * stack and the page scrolls as one. With `scroll`, and a frame above that
 * hands it a height, each column scrolls on its own on a desktop instead.
 * With `onExpandedChange`, the RecordHeading's button gives the record the
 * whole width.
 * The inspections and trucks screens both drew this with a dozen classes on
 * three Stacks, and the skeleton drew it a fourth time.
 */
export const ListDetailLayout = React.forwardRef<
  HTMLDivElement,
  ListDetailLayoutProps
>(function ListDetailLayout(
  {
    list,
    search,
    detail,
    scroll,
    bleed,
    expanded,
    onExpandedChange,
    className,
    ...props
  },
  ref
) {
  const expand = React.useMemo(
    () =>
      onExpandedChange ? { expanded: !!expanded, onExpandedChange } : null,
    [expanded, onExpandedChange]
  );
  return (
    <div
      ref={ref}
      className={cn(listDetailLayoutRecipe({ scroll }), className)}
      {...props}
    >
      <div className={listDetailLayoutListRecipe({ scroll, bleed, expanded })}>
        {search ? (
          <div className={listDetailLayoutSearchRecipe()}>{search}</div>
        ) : null}
        {list}
      </div>
      <div className={listDetailLayoutDetailRecipe({ scroll, expanded })}>
        <ExpandContext.Provider value={expand}>{detail}</ExpandContext.Provider>
      </div>
    </div>
  );
});
